import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, MapPin } from "lucide-react";
import api from "../api";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const staticMap = (center, pins, { zoom = 13, size = "640x300" } = {}) => {
  const markers = (pins || []).slice(0, 10)
    .map((p) => `&markers=color:0x16a34a%7C${p.lat},${p.lng}`).join("");
  return `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat},${center.lng}`
    + `&zoom=${zoom}&size=${size}&scale=2&maptype=roadmap${markers}&key=${KEY}`;
};

const Stat = ({ n, label }) => (
  <div className="flex-1">
    <p className="text-3xl font-extrabold text-[#0b3d2c] leading-none">{n}</p>
    <p className="text-[11px] uppercase tracking-[0.14em] text-[#6b7f76] mt-1.5 font-semibold">{label}</p>
  </div>
);

const Qr = ({ value, caption }) => (
  <div className="text-center shrink-0">
    <div className="bg-white p-2 rounded-lg border border-[#d8e2dc] inline-block">
      <QRCodeSVG value={value} size={92} level="M" />
    </div>
    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#0b3d2c] mt-2 max-w-[120px] mx-auto leading-tight">{caption}</p>
  </div>
);

// Two one-page sheets to hand across a counter: the city's own map with a visible gap where the
// shop would sit, and a plain explanation of what LytePress is. Printed from the browser, so what
// you see is exactly what comes out.
const CityPitch = () => {
  const { countrySlug, citySlug } = useParams();
  const [params] = useSearchParams();
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useSeo({ title: "Pitch sheets", noindex: true });

  useEffect(() => {
    api.get(`/admin/pitch/${countrySlug}/${citySlug}`, { params: { lang: params.get("lang") || "en" } })
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load that city."));
  }, [countrySlug, citySlug, params]);

  if (err) return <p className="text-sm text-muted-foreground" data-testid="pitch-error">{err}</p>;
  if (!d) return <div className="h-96 rounded-2xl bg-secondary animate-pulse" />;

  const money = (v, c) => (v == null ? "" : `${c === "TTD" ? "TT$" : c === "USD" ? "$" : `${c} `}${v}`);
  const sheet = { width: "190mm", minHeight: "272mm", padding: "16mm" };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #pitch-sheets, #pitch-sheets * { visibility: visible !important; }
          #pitch-sheets { position: absolute; left: 0; top: 0; width: 100%; margin: 0; }
          .no-print { display: none !important; }
          .sheet { page-break-after: always; box-shadow: none !important; border: 0 !important; }
          @page { size: A4; margin: 10mm; }
        }
      `}</style>

      <div className="no-print flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => window.print()} data-testid="pitch-print"
          className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-bold px-4 py-2.5 transition-colors">
          <Printer size={16} /> {t("pitch.print")}
        </button>
        <p className="text-sm text-muted-foreground">{t("pitch.printHint")}</p>
      </div>

      <div id="pitch-sheets" className="space-y-8">
        <article className="sheet bg-white text-[#0b3d2c] border border-[#d8e2dc] shadow-sm mx-auto flex flex-col"
          style={sheet} data-testid="pitch-sheet-city">
          <header className="flex items-start justify-between gap-6 pb-6 border-b-2 border-[#0b3d2c]">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#16a34a]">LytePress</p>
              <h1 className="text-[34px] leading-[1.05] font-extrabold mt-2 tracking-tight">
                {d.demo ? t("pitch.cityHeadingDemo", { city: d.city }) : t("pitch.cityHeading", { city: d.city })}
              </h1>
              <p className="text-[13px] text-[#4b5f57] mt-2 max-w-[110mm] leading-relaxed">
                {d.demo
                  ? t("pitch.citySubDemo", { city: d.city, country: d.country })
                  : t("pitch.citySub", { city: d.city, country: d.country })}
              </p>
            </div>
            <Qr value={d.cityUrl} caption={t("pitch.qrCityPage", { city: d.city })} />
          </header>

          <div className="flex gap-8 py-6 border-b border-[#e6ede9]">
            {d.demo ? (
              <>
                <Stat n={t("pitch.sellFreeN")} label={t("pitch.sellFree")} />
                <Stat n={t("pitch.sellFastN")} label={t("pitch.sellFast")} />
                <Stat n={t("pitch.sellLiveN")} label={t("pitch.sellLive")} />
              </>
            ) : (
              <>
                <Stat n={d.dealCount} label={t("pitch.statDeals")} />
                <Stat n={d.shopCount} label={t("pitch.statShops")} />
                <Stat n={d.topCategories[0]?.name || "—"} label={t("pitch.statTop")} />
              </>
            )}
          </div>

          <section className="pt-6">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6b7f76]">{t("pitch.mapTitle")}</h2>
            <div className="relative mt-3 overflow-hidden rounded-xl border border-[#d8e2dc]">
              {KEY ? (
                <img src={staticMap(d.center, d.pins)} alt={`Map of ${d.city}`} className="w-full block"
                  data-testid="pitch-city-map" />
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-[#6b7f76]">{t("pitch.noMapKey")}</div>
              )}
              <div className="absolute" style={{ right: "12%", bottom: "22%" }} data-testid="pitch-gap-marker">
                <div className="rounded-full border-2 border-dashed border-[#0b3d2c] bg-white/90 px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.1em] flex items-center gap-1.5">
                  <MapPin size={13} /> {t("pitch.yourSpot")}
                </div>
              </div>
            </div>
            {d.copy?.text && <p className="text-[12px] text-[#4b5f57] mt-3 leading-relaxed">{d.copy.text}</p>}
            {d.demo && (
              <p className="text-[11px] text-[#6b7f76] mt-2 italic" data-testid="pitch-demo-note">
                {t("pitch.demoNote", { city: d.city })}
              </p>
            )}
          </section>

          {d.samples.length > 0 && (
            <section className="pt-7">
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6b7f76]">
                {d.demo ? t("pitch.exampleTitle", { city: d.city }) : t("pitch.alreadyOn")}
              </h2>
              <ul className="mt-3 divide-y divide-[#e6ede9]" data-testid="pitch-samples">
                {d.samples.map((s, i) => (
                  <li key={i} className="py-2.5 flex items-baseline justify-between gap-4">
                    <span className="text-[13px] font-bold flex items-center gap-2">
                      {s.business}
                      {s.demo && (
                        <span className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-[#6b7f76] border border-[#d8e2dc] rounded px-1.5 py-0.5">
                          {t("pitch.exampleTag")}
                        </span>
                      )}
                    </span>
                    <span className="text-[13px] text-[#4b5f57] flex-1 truncate px-3">{s.title}</span>
                    <span className="text-[13px] font-extrabold text-[#16a34a] whitespace-nowrap">
                      {s.discount ? `${s.discount}% off` : money(s.price, s.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <footer className="mt-auto pt-8 flex items-end justify-between gap-6">
            <div className="max-w-[105mm]">
              <h3 className="text-[19px] font-extrabold leading-tight">{t("pitch.claimTitle", { city: d.city })}</h3>
              <p className="text-[12px] text-[#4b5f57] mt-2 leading-relaxed">{t("pitch.claimBody")}</p>
              <p className="text-[11px] text-[#6b7f76] mt-3 font-semibold">{d.joinUrl.replace(/^https?:\/\//, "")}</p>
            </div>
            <Qr value={d.joinUrl} caption={t("pitch.qrClaim")} />
          </footer>
        </article>

        <article className="sheet bg-white text-[#0b3d2c] border border-[#d8e2dc] shadow-sm mx-auto flex flex-col"
          style={sheet} data-testid="pitch-sheet-about">
          <header className="pb-6 border-b-2 border-[#0b3d2c]">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#16a34a]">LytePress</p>
            <h1 className="text-[34px] leading-[1.05] font-extrabold mt-2 tracking-tight">{t("pitch.aboutHeading")}</h1>
            <p className="text-[14px] text-[#4b5f57] mt-3 max-w-[140mm] leading-relaxed">{t("pitch.aboutLine")}</p>
          </header>

          <section className="pt-6">
            {KEY && (
              <img src={staticMap(d.center, d.pins, { zoom: 12, size: "640x260" })} alt="Sample map"
                className="w-full block rounded-xl border border-[#d8e2dc]" data-testid="pitch-sample-map" />
            )}
            <p className="text-[11px] text-[#6b7f76] mt-2 italic">{d.demo ? t("pitch.mapCaptionDemo") : t("pitch.mapCaption")}</p>
          </section>

          <div className="grid grid-cols-2 gap-8 pt-7">
            <section>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6b7f76]">{t("pitch.forShoppers")}</h2>
              <ul className="mt-3 space-y-3">
                {["pitch.shopper1", "pitch.shopper2", "pitch.shopper3"].map((k) => (
                  <li key={k} className="text-[13px] leading-relaxed flex gap-2.5">
                    <span className="text-[#16a34a] font-extrabold">—</span><span>{t(k)}</span>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6b7f76]">{t("pitch.forShops")}</h2>
              <ul className="mt-3 space-y-3">
                {["pitch.shop1", "pitch.shop2", "pitch.shop3"].map((k) => (
                  <li key={k} className="text-[13px] leading-relaxed flex gap-2.5">
                    <span className="text-[#16a34a] font-extrabold">—</span><span>{t(k)}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section className="mt-8 rounded-xl bg-[#f3f8f5] border border-[#d8e2dc] p-5">
            <h2 className="text-[15px] font-extrabold">{t("pitch.howTitle")}</h2>
            <ol className="mt-3 grid grid-cols-3 gap-5">
              {["pitch.how1", "pitch.how2", "pitch.how3"].map((k, i) => (
                <li key={k} className="text-[12px] leading-relaxed">
                  <span className="block text-[#16a34a] font-extrabold text-[22px] leading-none">{i + 1}</span>
                  <span className="block mt-1.5">{t(k)}</span>
                </li>
              ))}
            </ol>
          </section>

          <footer className="mt-auto pt-8 flex items-end justify-between gap-6">
            <div className="max-w-[105mm]">
              <h3 className="text-[19px] font-extrabold leading-tight">{t("pitch.startTitle")}</h3>
              <p className="text-[12px] text-[#4b5f57] mt-2 leading-relaxed">{t("pitch.startBody")}</p>
              <p className="text-[11px] text-[#6b7f76] mt-3 font-semibold">{d.siteUrl.replace(/^https?:\/\//, "")}</p>
            </div>
            <Qr value={d.joinUrl} caption={t("pitch.qrStart")} />
          </footer>
        </article>
      </div>
    </>
  );
};

export default CityPitch;
